import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MainLayout from '../../components/MainLayout';
import Modal from '../../components/Modal';
import ManagementHeader from '../management/ManagementHeader';
import { useTranslation } from '../../context/LanguageContext';
import {
  fetchAiAdminMeta, fetchAiConnections, createAiConnection, updateAiConnection, deleteAiConnection,
  testAiConnection, fetchAiConnectionModels, fetchAiFeatures, assignAiFeature, fetchAiCalls, fetchAiUsage
} from '../../services/api';
import '../management/management.css';
import './AiManagePage.css';

/* AI 분석 > AI 관리 (MES AI 관리와 같은 구성)
 *  - 모델 연결: OpenAI 호환(DGX·LM Studio 등) / Anthropic Claude 연결을 등록하고 키를 암호화 저장, 테스트
 *  - 기능별 모델 배정: 질문 해석·SQL 생성 / 답변 작성 / 분석 / 차트 / 보고서 각각 어느 연결을 쓸지 선택 (즉시 반영)
 *  - 호출 이력: 오늘·이번 달 호출·토큰·실패, 최근 호출 목록
 * 데이터는 AI 서비스(/api/ai-admin) 가 public.ai_connections 등에 둔다. 전 테넌트 공통 설정. */

const EMPTY_FORM = {
  conn_key: '', name: '', provider: 'OPENAI_COMPATIBLE', api_base: '', model_name: '', api_key: '', clear_api_key: false,
  description: '', sort_order: 0, timeout_ms: '', max_tokens: '', temperature: '', effort: 'low', is_active: true,
  workspace_id: '',
};

const PROVIDER_DEFAULT_BASE = {
  OPENAI_COMPATIBLE: 'http://175.116.57.115:18000/v1',
  ANTHROPIC: 'https://api.anthropic.com',
};

function fmtNum(n) {
  return (n === null || n === undefined) ? '-' : Number(n).toLocaleString();
}

function fmtTime(iso) {
  if (!iso) return '-';
  return iso.replace('T', ' ').slice(0, 19);
}

function AiManagePage() {
  const { t } = useTranslation();
  const tr = useCallback((k, params) => t(`pages.ai.aiManage.${k}`, params), [t]);

  const [tab, setTab] = useState('connections');
  const [meta, setMeta] = useState({ providers: [], efforts: [], features: [] });
  const [conns, setConns] = useState([]);
  const [features, setFeatures] = useState([]);
  const [calls, setCalls] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const [modal, setModal] = useState(null); // { mode: 'add' | 'edit', id?, values }
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [modelOptions, setModelOptions] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);

  const [testing, setTesting] = useState({});
  const [testResult, setTestResult] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [assigning, setAssigning] = useState({});

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [m, c, f] = await Promise.all([fetchAiAdminMeta(), fetchAiConnections(), fetchAiFeatures()]);
      setMeta(m);
      setConns(c);
      setFeatures(f);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCalls = useCallback(async () => {
    try {
      const [u, c] = await Promise.all([fetchAiUsage(), fetchAiCalls(100)]);
      setUsage(u);
      setCalls(c);
    } catch (e) {
      setError(e.message || String(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'calls') loadCalls(); }, [tab, loadCalls]);

  const connById = useMemo(() => Object.fromEntries(conns.map((c) => [c.id, c])), [conns]);
  const activeConns = useMemo(() => conns.filter((c) => c.is_active), [conns]);

  // ---------- 연결 폼 ----------
  const openAdd = () => {
    setFormError('');
    setModelOptions([]);
    setModal({ mode: 'add', values: { ...EMPTY_FORM, api_base: PROVIDER_DEFAULT_BASE.OPENAI_COMPATIBLE, sort_order: conns.length + 1 } });
  };

  const openEdit = (c) => {
    setFormError('');
    setModelOptions([]);
    setModal({
      mode: 'edit', id: c.id, hasKey: c.has_key,
      values: {
        conn_key: c.conn_key, name: c.name, provider: c.provider, api_base: c.api_base || '', model_name: c.model_name,
        api_key: '', clear_api_key: false, description: c.description || '', sort_order: c.sort_order ?? 0,
        timeout_ms: c.timeout_ms ?? '', max_tokens: c.max_tokens ?? '', temperature: c.temperature ?? '',
        effort: c.effort || 'low', is_active: !!c.is_active, workspace_id: c.workspace_id || '',
      },
    });
  };

  const setField = (k, v) => setModal((m) => ({ ...m, values: { ...m.values, [k]: v } }));

  const onProviderChange = (p) => {
    setModal((m) => {
      const v = { ...m.values, provider: p };
      if (!v.api_base || Object.values(PROVIDER_DEFAULT_BASE).includes(v.api_base)) v.api_base = PROVIDER_DEFAULT_BASE[p] || '';
      return { ...m, values: v };
    });
    setModelOptions([]);
  };

  const toPayload = (v) => ({
    conn_key: v.conn_key, name: v.name, provider: v.provider, api_base: v.api_base, model_name: v.model_name,
    api_key: v.api_key || null, clear_api_key: !!v.clear_api_key, description: v.description,
    sort_order: Number(v.sort_order) || 0,
    timeout_ms: v.timeout_ms === '' ? null : Number(v.timeout_ms),
    max_tokens: v.max_tokens === '' ? null : Number(v.max_tokens),
    temperature: v.provider === 'ANTHROPIC' || v.temperature === '' ? null : Number(v.temperature),
    effort: v.provider === 'ANTHROPIC' ? (v.effort || 'low') : null,
    is_active: !!v.is_active,
    workspace_id: v.provider === 'ANTHROPIC' ? ((v.workspace_id || '').trim() || null) : null,
  });

  const submitForm = async () => {
    if (!modal) return;
    const v = modal.values;
    if (!v.name.trim() || !v.model_name.trim() || (modal.mode === 'add' && !v.conn_key.trim())) {
      setFormError(tr('form.required'));
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      if (modal.mode === 'add') await createAiConnection(toPayload(v));
      else await updateAiConnection(modal.id, toPayload(v));
      setModal(null);
      showToast(tr('msg.saved'));
      await load();
    } catch (e) {
      setFormError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const loadModels = async () => {
    if (!modal || modal.mode !== 'edit') return;
    setLoadingModels(true);
    setFormError('');
    try {
      const list = await fetchAiConnectionModels(modal.id);
      setModelOptions(list);
      if (!list.length) setFormError(tr('form.noModels'));
    } catch (e) {
      setFormError(e.message || String(e));
    } finally {
      setLoadingModels(false);
    }
  };

  // ---------- 테스트 / 삭제 ----------
  const runTest = async (c) => {
    setTesting((s) => ({ ...s, [c.id]: true }));
    try {
      const r = await testAiConnection(c.id);
      setTestResult((s) => ({ ...s, [c.id]: r }));
    } catch (e) {
      setTestResult((s) => ({ ...s, [c.id]: { success: false, message: e.message || String(e) } }));
    } finally {
      setTesting((s) => ({ ...s, [c.id]: false }));
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteAiConnection(confirmDelete.id);
      showToast(tr('msg.deleted'));
      await load();
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  // ---------- 기능 배정 ----------
  const onAssign = async (code, value) => {
    const connectionId = value === '' ? null : Number(value);
    setAssigning((s) => ({ ...s, [code]: true }));
    try {
      const f = await assignAiFeature(code, connectionId);
      setFeatures(f);
      const c = await fetchAiConnections();
      setConns(c);
      showToast(tr('msg.assigned'));
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setAssigning((s) => ({ ...s, [code]: false }));
    }
  };

  // ---------- 렌더 ----------
  const providerLabel = (p) => tr(`provider.${p}`);

  const keyBadge = (c) => {
    if (c.has_key) return <span className="mg-badge success">{tr('card.keySaved')}</span>;
    if (c.env_key) return <span className="mg-badge info">{tr('card.keyEnv')}</span>;
    return <span className="mg-badge warning">{tr('card.keyNone')}</span>;
  };

  const renderCard = (c) => {
    const r = testResult[c.id];
    return (
      <div key={c.id} className={`aim-card ${c.is_active ? '' : 'inactive'}`}>
        <div className="aim-card-head">
          <span className="material-symbols-outlined aim-icon">
            {c.provider === 'ANTHROPIC' ? 'auto_awesome' : 'dns'}
          </span>
          <span className="aim-name" title={c.name}>{c.name}</span>
          <span className={`mg-badge ${c.is_active ? 'success' : 'neutral'}`}>{c.is_active ? tr('card.active') : tr('card.inactive')}</span>
        </div>
        <div className="aim-model">{c.model_name}</div>
        <div className="aim-meta">{providerLabel(c.provider)} · {c.conn_key}</div>
        {c.api_base && <div className="aim-meta">{c.api_base}</div>}
        {c.workspace_id && <div className="aim-meta">{tr('card.workspace')}: {c.workspace_id}</div>}
        <div className="aim-tags">
          {keyBadge(c)}
          {c.features.length === 0
            ? <span className="aim-tag empty">{tr('card.noFeatures')}</span>
            : c.features.map((f) => <span key={f} className="aim-tag" title={tr(`feature.${f}`)}>{tr(`featureShort.${f}`)}</span>)}
        </div>
        {c.description && <div className="aim-meta">{c.description}</div>}
        {r && (
          r.success ? (
            <div className="aim-test ok">
              {r.message}{r.latency_ms !== undefined ? ` (${fmtNum(r.latency_ms)} ms)` : ''}
            </div>
          ) : (
            // 실패: 할 일을 한 줄로 보여 주고, 오류 원문은 접어 둔다
            <div className="aim-test fail">
              <div className="aim-test-summary">{r.hint || tr('card.testFailed')}</div>
              <details>
                <summary>{tr('card.errorDetail')}</summary>
                <div className="aim-test-raw">{r.message}</div>
              </details>
            </div>
          )
        )}
        <div className="aim-card-actions">
          <button type="button" className="mg-btn secondary" onClick={() => runTest(c)} disabled={!!testing[c.id]}>
            <span className="material-symbols-outlined">network_check</span>
            {testing[c.id] ? tr('card.testing') : tr('card.test')}
          </button>
          <button type="button" className="mg-btn secondary" onClick={() => openEdit(c)}>
            <span className="material-symbols-outlined">edit</span>
            {tr('card.edit')}
          </button>
          <button type="button" className="mg-btn danger" onClick={() => setConfirmDelete(c)} disabled={c.features.length > 0}
            title={c.features.length > 0 ? tr('card.deleteBlocked') : ''}>
            <span className="material-symbols-outlined">delete</span>
            {tr('card.delete')}
          </button>
        </div>
      </div>
    );
  };

  const renderAssign = () => (
    <div className="mg-panel aim-assign">
      <div className="mg-panel-title">{tr('assign.title')}</div>
      <p className="aim-help">{tr('assign.help')}</p>
      <div className="mg-table-wrap">
        <table className="mg-table">
          <thead>
            <tr>
              <th className="left">{tr('assign.feature')}</th>
              <th className="left">{tr('assign.connection')}</th>
              <th>{tr('assign.updated')}</th>
            </tr>
          </thead>
          <tbody>
            {features.map((f) => (
              <tr key={f.code}>
                <td className="left">
                  <span className="aim-feature-label">{tr(`feature.${f.code}`)}</span>
                  <span className="aim-feature-group">{f.code}</span>
                </td>
                <td className="left">
                  <select className="mg-select" value={f.connection_id ?? ''} disabled={!!assigning[f.code]}
                    onChange={(e) => onAssign(f.code, e.target.value)}>
                    <option value="">{tr('assign.none')}</option>
                    {activeConns.map((c) => (
                      <option key={c.id} value={c.id}>{c.name} · {c.model_name}</option>
                    ))}
                    {f.connection_id && !activeConns.some((c) => c.id === f.connection_id) && connById[f.connection_id] && (
                      <option value={f.connection_id}>{connById[f.connection_id].name} ({tr('card.inactive')})</option>
                    )}
                  </select>
                </td>
                <td className="aim-updated">
                  <span className="mg-muted">{fmtTime(f.updated_at)}{f.updated_by ? ` · ${f.updated_by}` : ''}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderStats = () => {
    if (!usage) return null;
    const cells = [
      [tr('calls.todayCalls'), usage.today.calls], [tr('calls.todayTokens'), usage.today.tokens],
      [tr('calls.monthCalls'), usage.month.calls], [tr('calls.monthTokens'), usage.month.tokens],
      [tr('calls.monthFailures'), usage.month.failures], [tr('calls.avgLatency'), `${fmtNum(usage.month.avg_latency_ms)} ms`],
    ];
    return (
      <div className="mg-stats">
        {cells.map(([label, value]) => (
          <div key={label} className="mg-stat">
            <div className="mg-stat-label">{label}</div>
            <div className="mg-stat-value">{typeof value === 'number' ? fmtNum(value) : value}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderCalls = () => (
    <div className="mg-panel fill aim-calls">
      <div className="mg-toolbar">
        <div className="mg-panel-title">{tr('calls.title')}</div>
        <div className="mg-toolbar-right">
          <button type="button" className="mg-btn secondary" onClick={loadCalls}>
            <span className="material-symbols-outlined">refresh</span>
            {t('common.refresh')}
          </button>
        </div>
      </div>
      {renderStats()}
      {usage && usage.month_by_connection.length > 0 && (
        <p className="aim-help">
          {tr('calls.byConnection')}: {usage.month_by_connection.map((m) => `${m.name || m.conn_key || '-'} ${fmtNum(m.calls)}회 / ${fmtNum(m.tokens)} tok`).join(' · ')}
        </p>
      )}
      <div className="mg-table-wrap">
        <table className="mg-table">
          <thead>
            <tr>
              <th>{tr('calls.time')}</th>
              <th className="left">{tr('calls.feature')}</th>
              <th className="left">{tr('calls.connection')}</th>
              <th className="left">{tr('calls.model')}</th>
              <th>{tr('calls.tenant')}</th>
              <th>{tr('calls.inTokens')}</th>
              <th>{tr('calls.outTokens')}</th>
              <th>{tr('calls.latency')}</th>
              <th>{tr('calls.result')}</th>
            </tr>
          </thead>
          <tbody>
            {calls.length === 0 ? (
              <tr className="mg-empty-row"><td className="mg-empty" colSpan={9}>{tr('calls.empty')}</td></tr>
            ) : calls.map((c) => (
              <tr key={c.id}>
                <td>{fmtTime(c.created_at)}</td>
                <td className="left">{tr(`featureShort.${c.feature_code}`) || c.feature_code}</td>
                <td className="left">{c.connection_name || c.conn_key || '-'}</td>
                <td className="left aim-model">{c.model_name || '-'}</td>
                <td>{c.tenant || '-'}</td>
                <td className="num">{fmtNum(c.prompt_tokens)}</td>
                <td className="num">{fmtNum(c.completion_tokens)}</td>
                <td className="num">{fmtNum(c.latency_ms)}</td>
                <td className={c.success ? '' : 'err'} title={c.error_message || ''}>
                  {c.success ? <span className="mg-badge success">OK</span> : <span className="mg-badge error">{c.error_message || tr('calls.failed')}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderForm = () => {
    if (!modal) return null;
    const v = modal.values;
    const isAnthropic = v.provider === 'ANTHROPIC';
    return (
      <>
        <div className="mg-modal-overlay" onClick={() => setModal(null)}></div>
        <div className="mg-modal" role="dialog" aria-modal="true" style={{ maxWidth: 640 }}>
          <div className="mg-modal-head">
            <h3 className="mg-modal-title">{modal.mode === 'add' ? tr('form.addTitle') : tr('form.editTitle')}</h3>
            <button type="button" className="mg-icon-btn" onClick={() => setModal(null)}>
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
          <div className="mg-modal-body">
            <div className="mg-form editing">
              <div className="aim-form-row">
                <label className="mg-field">
                  {tr('form.connKey')} <span className="mg-required">*</span>
                  <input className="mg-input" value={v.conn_key} disabled={modal.mode === 'edit'} placeholder="claude, inhouse-llm"
                    onChange={(e) => setField('conn_key', e.target.value)} />
                </label>
                <label className="mg-field">
                  {tr('form.name')} <span className="mg-required">*</span>
                  <input className="mg-input" value={v.name} onChange={(e) => setField('name', e.target.value)} />
                </label>
              </div>
              <div className="aim-form-row">
                <label className="mg-field">
                  {tr('form.provider')}
                  <select className="mg-select" value={v.provider} onChange={(e) => onProviderChange(e.target.value)}>
                    {meta.providers.map((p) => <option key={p} value={p}>{providerLabel(p)}</option>)}
                  </select>
                </label>
                <label className="mg-field">
                  {tr('form.apiBase')}
                  <input className="mg-input" value={v.api_base} placeholder={PROVIDER_DEFAULT_BASE[v.provider] || ''}
                    onChange={(e) => setField('api_base', e.target.value)} />
                </label>
              </div>
              <label className="mg-field">
                {tr('form.modelName')} <span className="mg-required">*</span>
                <div className="aim-inline">
                  <input className="mg-input" list="aim-model-options" value={v.model_name} onChange={(e) => setField('model_name', e.target.value)} />
                  <datalist id="aim-model-options">
                    {modelOptions.map((m) => <option key={m} value={m} />)}
                  </datalist>
                  {modal.mode === 'edit' && (
                    <button type="button" className="mg-btn secondary" onClick={loadModels} disabled={loadingModels}>
                      {loadingModels ? '…' : tr('form.loadModels')}
                    </button>
                  )}
                </div>
                {modelOptions.length > 0 && <span className="mg-muted">{tr('form.modelsLoaded', { n: modelOptions.length })}</span>}
              </label>
              <label className="mg-field">
                {tr('form.apiKey')}
                <input className="mg-input" type="password" autoComplete="new-password" value={v.api_key}
                  placeholder={modal.hasKey ? tr('form.apiKeyKeep') : tr('form.apiKeyEnv')}
                  onChange={(e) => setField('api_key', e.target.value)} />
                {modal.hasKey && (
                  <label className="aim-inline" style={{ fontWeight: 400, marginTop: 4 }}>
                    <input type="checkbox" checked={!!v.clear_api_key} onChange={(e) => setField('clear_api_key', e.target.checked)} />
                    {tr('form.clearKey')}
                  </label>
                )}
              </label>
              {isAnthropic && (
                <>
                  <label className="mg-field">
                    {tr('form.workspaceId')}
                    <input className="mg-input" value={v.workspace_id} placeholder="wrkspc_…" autoComplete="off"
                      onChange={(e) => setField('workspace_id', e.target.value)} />
                  </label>
                  <p className="aim-help">{tr('form.workspaceHint')}</p>
                </>
              )}
              <div className="aim-form-row three">
                <label className="mg-field">
                  {tr('form.timeoutMs')}
                  <input className="mg-input" type="number" min="1000" step="1000" value={v.timeout_ms} placeholder="180000"
                    onChange={(e) => setField('timeout_ms', e.target.value)} />
                </label>
                <label className="mg-field">
                  {tr('form.maxTokens')}
                  <input className="mg-input" type="number" min="1" value={v.max_tokens} placeholder={tr('form.default')}
                    onChange={(e) => setField('max_tokens', e.target.value)} />
                </label>
                {isAnthropic ? (
                  <label className="mg-field">
                    {tr('form.effort')}
                    <select className="mg-select" value={v.effort || 'low'} onChange={(e) => setField('effort', e.target.value)}>
                      {meta.efforts.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </label>
                ) : (
                  <label className="mg-field">
                    {tr('form.temperature')}
                    <input className="mg-input" type="number" min="0" max="2" step="0.1" value={v.temperature} placeholder={tr('form.default')}
                      onChange={(e) => setField('temperature', e.target.value)} />
                  </label>
                )}
              </div>
              <div className="aim-form-row">
                <label className="mg-field">
                  {tr('form.sortOrder')}
                  <input className="mg-input" type="number" value={v.sort_order} onChange={(e) => setField('sort_order', e.target.value)} />
                </label>
                <label className="mg-field">
                  {tr('form.isActive')}
                  <label className="aim-inline" style={{ fontWeight: 400, marginTop: 8 }}>
                    <input type="checkbox" checked={!!v.is_active} onChange={(e) => setField('is_active', e.target.checked)} />
                    {v.is_active ? tr('card.active') : tr('card.inactive')}
                  </label>
                </label>
              </div>
              <label className="mg-field">
                {tr('form.description')}
                <textarea className="mg-textarea" rows={2} value={v.description} onChange={(e) => setField('description', e.target.value)} />
              </label>
              {isAnthropic && <p className="aim-help">{tr('form.anthropicHint')}</p>}
              {formError && <p className="aim-error">{formError}</p>}
            </div>
          </div>
          <div className="mg-modal-actions">
            <button type="button" className="mg-btn secondary" onClick={() => setModal(null)}>{t('common.cancel')}</button>
            <button type="button" className="mg-btn primary" onClick={submitForm} disabled={saving}>
              {saving ? '…' : t('common.save')}
            </button>
          </div>
        </div>
      </>
    );
  };

  return (
    <MainLayout>
      <div className="mg-page">
        <ManagementHeader rootLabel={t('sidebar.ai.title')} icon="text_fields_alt" crumbs={[{ label: t('sidebar.ai.manage') }]}>
          <button type="button" className="mg-btn secondary" onClick={() => (tab === 'calls' ? loadCalls() : load())}>
            <span className="material-symbols-outlined">refresh</span>
            {t('common.refresh')}
          </button>
          <button type="button" className="mg-btn primary" onClick={openAdd}>
            <span className="material-symbols-outlined">add</span>
            {tr('addBtn')}
          </button>
        </ManagementHeader>

        <div className="mg-tabs">
          <button type="button" className={`mg-tab ${tab === 'connections' ? 'active' : ''}`} onClick={() => setTab('connections')}>{tr('tabs.connections')}</button>
          <button type="button" className={`mg-tab ${tab === 'calls' ? 'active' : ''}`} onClick={() => setTab('calls')}>{tr('tabs.calls')}</button>
        </div>

        {error && (
          <div className="mg-notice" role="alert">
            <span className="material-symbols-outlined">error</span>
            <p>{error}</p>
          </div>
        )}

        {tab === 'connections' && (
          <div className="aim-layout">
            <div className="mg-panel">
              <div className="mg-panel-title">{tr('connections.title')}</div>
              <p className="aim-help">{tr('connections.help')}</p>
              {loading ? (
                <div className="mg-loading">{t('common.loading')}</div>
              ) : conns.length === 0 ? (
                <div className="mg-empty">{tr('connections.empty')}</div>
              ) : (
                <div className="aim-cards">{conns.map(renderCard)}</div>
              )}
            </div>
            {renderAssign()}
          </div>
        )}

        {tab === 'calls' && renderCalls()}

        {renderForm()}

        <Modal
          isOpen={!!confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onConfirm={doDelete}
          title={tr('card.delete')}
          message={confirmDelete ? tr('msg.confirmDelete', { name: confirmDelete.name }) : ''}
          type="danger"
        />

        {toast && <div className="aim-toast">{toast}</div>}
      </div>
    </MainLayout>
  );
}

export default AiManagePage;
